import React, { useEffect, useState, useMemo } from 'react'
import { ErrorNotification } from 'services/helpers/helpers'
import { documentToReactComponents } from '@contentful/rich-text-react-renderer'
import { getContent } from 'services/ContentfulApi'
import { getContentfulNodeRenderers } from 'services/helpers/contentful/getContentfulNodeRenderers'
import { ContentfulPageContentWrapper } from 'components/Contentful/ContentfulPageContentWrapper'
import { useFeatureFlags } from 'hooks/useFeatureFlags'
import { ContentfulRenderHeading } from 'components/Contentful/ContentfulRenderHeading'

export const useContentful = (contentfulUrl: string, contentfulToken: string, language: string) => {
  const [pages, setPages] = useState<any[]>([])
  const [assets, setAssets] = useState<any[]>([])
  const [contacts, setContacts] = useState<any[] | null>(null)
  const [posts, setPosts] = useState<any[]>([])
  const [isAuthLoading, setIsAuthLoading] = useState<boolean>(false)
  const [isContentfulLoading, setIsContentfulLoading] = useState<boolean>(true)
  const { IS_HOSPITAL } = useFeatureFlags()

  useEffect(() => {
    const fetchData = async () => {
      try {
        const pageResult = await getContent(contentfulUrl, contentfulToken, 'page', language)

        const filteredPages = pageResult.items.filter((page) => {
          const showInHospitalInstance = page?.fields?.showInHospitalInstance
          return showInHospitalInstance === undefined
            ? true
            : IS_HOSPITAL
            ? showInHospitalInstance === true
            : showInHospitalInstance !== true
        })

        const contactResult = await getContent(
          contentfulUrl,
          contentfulToken,
          'contactUs',
          language
        )
        const postResult = await getContent(contentfulUrl, contentfulToken, 'newsPost', language)

        setAssets(pageResult.includes?.Asset)
        setPages(filteredPages)
        setPosts(postResult.items)
        setContacts(contactResult.items)
      } catch (error) {
        ErrorNotification(
          'Could not load content',
          'Some content is not accessible. Try again later'
        )
      } finally {
        setIsContentfulLoading(false)
      }
    }

    fetchData()
  }, [contentfulUrl, contentfulToken, language, IS_HOSPITAL])

  const nodeRenderingOptions = useMemo(() => getContentfulNodeRenderers(assets), [assets])

  const renderPageContent = (document: any) => {
    const toc: { id: string; text: string; level: number }[] = []

    const renderHeading = ContentfulRenderHeading(toc)

    const options = {
      ...nodeRenderingOptions,
      renderNode: {
        ...nodeRenderingOptions.renderNode,
        'heading-1': renderHeading(1),
        'heading-2': renderHeading(2),
        'heading-3': renderHeading(3),
      },
    }

    const content = documentToReactComponents(document, options)

    return {
      toc,
      content: <ContentfulPageContentWrapper>{content}</ContentfulPageContentWrapper>,
    }
  }

  return {
    pages,
    posts,
    contacts,
    isContentfulLoading,
    isAuthLoading,
    setIsAuthLoading,
    renderPageContent,
  }
}
